import { DateTime } from "luxon";

const FORMAT = {
    ISO: "yyyy-MM-dd",
    PT_BR: "dd/MM/yyyy"
}

const WEEKDAYS = {
    MONDAY: 1,
    TUESDAY: 2,
    WEDNESDAY: 3,
    THURSDAY: 4,
    FRIDAY: 5,
    SATURDAY: 6,
    SUNDAY: 7
};

export default class UtilDateTime{

    //==================================================================//
    //========================= Date Time ==============================//
    //==================================================================//

    static dateTimeNow = () => {
        
        return DateTime.now();
        
    }

    static dateTimeToday = () => {
        
        return DateTime.now().startOf("day");
        
    }

    static dateTimeTomorrow = (options = {}) => {

        let {
            dateTime = this.dateTimeToday(),
        } = options;
        
        return this.toDateTime({dateTime}).plus({ days: 1 }).startOf("day");
        
    }

    static dateTimeYesterday = (options = {}) => {

        let {
            dateTime = this.dateTimeToday(),
        } = options;
        
        return this.toDateTime({dateTime}).minus({ days: 1 }).startOf("day");
        
    }

    //==================================================================//
    //======================== To Date Time ============================//
    //==================================================================//

    static toDateTime = (options = {}) =>{
 
        let {
            dateTime = "",
        } = options;



        //==================================================================//
        //========================= Date Time ==============================//
        //==================================================================//
        // Se já é uma DataTime do Luxon.
        // Retorna ela (inicie a hora em 00:00)
        if (DateTime.isDateTime(dateTime)) {
            return dateTime.startOf("day");
        }



        //==================================================================//
        //====================== ISO DateString ============================//
        //==================================================================//
        // Se é uma ISODateString String.
        // Converta ela em Luxon
        // Retorna ela (inicie a hora em 00:00)
        if (this.isISODateString({dateTime})) {
            return DateTime.fromISO(dateTime)?.startOf("day");
        }



        //==================================================================//
        //=========================== JSDate ===============================//
        //==================================================================//
        // Se é uma JSDate String.
        // Converta ela em Luxon
        // Retorna ela (inicie a hora em 00:00)
        if (this.isJSDate({dateTime})) {
            return DateTime.fromJSDate({dateTime})?.startOf("day");
        }



        //==================================================================//
        //======================= Date Time String =========================//
        //==================================================================//
        // Se for uma DateTime String do Luxon.
        // Procura um formato valido para ela.
        // Retorna ela (inicie a hora em 00:00)
        const formats = Object.values(FORMAT);
        dateTime = formats
            .map(fmt => DateTime.fromFormat(dateTime, fmt))
            .find(dt => dt.isValid);

        return dateTime?.startOf("day");

    }

    //==================================================================//
    //========================== To Format =============================//
    //==================================================================//

    static toFormatISO = (options = {}) =>{
 
        let {
            dateTime = "",
        } = options;

        return this.toDateTime({dateTime})?.toFormat(FORMAT.ISO);
       
    }

    static toFormatBrazil = (options = {}) =>{
 
        let {
            dateTime = "",
        } = options;

        return this.toDateTime({dateTime})?.toFormat(FORMAT.PT_BR);
       
    }

    static toFormatJSDate = (options = {}) =>{
 
        let {
            dateTime = "",
        } = options;

        return this.toDateTime({dateTime})?.toJSDate();
       
    }

    static toFormatUTC = (options = {}) =>{
 
        let {
            dateTime = "",
        } = options;

        return this.toDateTime({dateTime})?.toUTC()?.toISO({ suppressMilliseconds: true });
       
    }

    //==================================================================//
    //============================ IS ==================================//
    //==================================================================//

    static isISODateString = (options = {}) =>{
 
        let {
            dateTime = "",
        } = options;

        dateTime = DateTime.fromISO(dateTime);
        return dateTime?.isValid;
       
    }

    static isJSDate = (options = {}) =>{
 
        let {
            dateTime = "",
        } = options;

        dateTime = DateTime.fromJSDate(dateTime);
        return dateTime?.isValid;
       
    }

    static isValid = (options = {}) =>{
 
        let {
            dateTime = "",
        } = options;

        if(!this.toDateTime({dateTime})?.isValid){
            return false;
        }

        return true;
       
    }

    static isInvalid = (options = {}) =>{
 
        let {
            dateTime = "",
        } = options;

        if(!this.toDateTime({dateTime})?.isValid){
            return true;
        }

        return false;
       
    }

    //==================================================================//
    //============================ Now =================================//
    //==================================================================//

    static now = (options = {}) => {

        let {
            format = FORMAT.ISO,
        } = options;

        return this.dateTimeNow()?.toFormat(format);

    }

    static nowISO = () => {

        return this.now({
            format: FORMAT.ISO
        });
        
    }

    static nowBrazil = ( ) => {

        return this.now({
            format: FORMAT.PT_BR
        });

    }

    static nowUTC = () => {

        return this.dateTimeNow()?.toUTC()?.toISO({ suppressMilliseconds: true });
        
    }

    //==================================================================//
    //=========================== Today ================================//
    //==================================================================//

    static today = (options = {}) => {
        
        let {
            format = FORMAT.ISO,
        } = options;

        return this.dateTimeToday()?.toFormat(format);
        
    }

    static todayISO = () => {
        
        return this.today({
            format: FORMAT.ISO
        });
        
    }

    static todayBrazil = () => {

        return this.today({
            format: FORMAT.PT_BR
        });
        
    }

    static todayUTC = () => {

        return this.dateTimeToday()?.toUTC()?.toISO({ suppressMilliseconds: true });

    }

    //==================================================================//
    //========================= Tomorrow ===============================//
    //==================================================================//

    static tomorrow = (options = {}) => {

        let {
            dateTime = this.dateTimeToday(),
            format = FORMAT.ISO,
        } = options;

        return this.toDateTime({dateTime})?.plus({ days: 1 })?.toFormat(format);

    };

    static tomorrowISO = (options = {}) => {

        options.format = FORMAT.ISO
        return this.tomorrow(options);
        
    }

    static tomorrowBrazil = (options = {}) => {

        options.format = FORMAT.PT_BR
        return this.tomorrow(options);
        
    }

    static tomorrowUTC = (options = {}) =>{
 
        let {
            dateTime = this.dateTimeTomorrow(),
        } = options;

        return this.toDateTime({dateTime})?.toUTC()?.toISO({ suppressMilliseconds: true });
       
    }

    //==================================================================//
    //======================== Yesterday ===============================//
    //==================================================================//

    static yesterday = (options = {}) => {

        const {
            dateTime = this.dateTimeToday(),
            format = FORMAT.ISO,
        } = options;

        return this.toDateTime({dateTime})?.minus({ days: 1 })?.toFormat(format);

    };

    static yesterdayISO = (options = {}) => {
        
        options.format = FORMAT.ISO
        return this.yesterday(options);
        
    }

    static yesterdayBrazil = (options = {}) => {

        options.format = FORMAT.PT_BR
        return this.yesterday(options);
        
    }

    static yesterdayUTC = (options = {}) => {

        let {
            dateTime = this.dateTimeYesterday(),
        } = options;

        return this.toDateTime({dateTime})?.toUTC()?.toISO({ suppressMilliseconds: true });

    }

    //==================================================================//
    //======================= Next Weekday =============================//
    //==================================================================//

    static nextWeekday = (options = {}) => {

        let {
            dateTime = this.dateTimeToday(),
            weekday = "Monday",
            format = FORMAT.ISO,
            weeks = 0,
        } = options;

        dateTime = this.toDateTime({ dateTime });
        const weekdayNumber = WEEKDAYS[weekday.toUpperCase()];
        const currentWeekday = dateTime.weekday;

        // Diferença de dias até o próximo dia da semana desejado
        let daysToAdd = (weekdayNumber + 7 - currentWeekday) % 7;

        // Soma múltiplas semanas, se necessário
        daysToAdd += 7 * weeks;

        const nextDate = dateTime.plus({ days: daysToAdd });
        return nextDate.toFormat(format);
        
    }

    static nextWeekdayISO = (options = {}) =>{

        options.format = FORMAT.ISO;
        return this.nextWeekday(options);

    }

    static nextWeekdayBrazil = (options = {}) =>{

        options.format = FORMAT.PT_BR;
        return this.nextWeekday(options);

    }

    static nextWeekdayUTC = (options = {}) =>{

        let dateTime = this.nextWeekday(options);

        return this.toDateTime({dateTime})?.toUTC()?.toISO({ suppressMilliseconds: true });

    }

    //==================================================================//
    //====================== Next [Day] ISO ============================//
    //==================================================================//

    static nextSundayISO = (options = {}) =>{

        options.weekday = "Sunday";
        return this.nextWeekdayISO(options);

    }

    static nextMondayISO = (options = {}) =>{

        options.weekday = "Monday";
        return this.nextWeekdayISO(options);

    }

    static nextTuesdayISO = (options = {}) =>{

        options.weekday = "Tuesday";
        return this.nextWeekdayISO(options);

    }

    static nextWednesdayISO = (options = {}) =>{

        options.weekday = "Wednesday";
        return this.nextWeekdayISO(options);

    }

    static nextThursdayISO = (options = {}) =>{

        options.weekday = "Thursday";
        return this.nextWeekdayISO(options);

    }

    static nextFridayISO = (options = {}) =>{

        options.weekday = "Friday";
        return this.nextWeekdayISO(options);

    }

    static nextSaturdayISO = (options = {}) =>{

        options.weekday = "Saturday";
        return this.nextWeekdayISO(options);

    }

    //==================================================================//
    //==================== Next [Day] Brazil ===========================//
    //==================================================================//

    static nextMondayBrazil = (options = {}) =>{

        options.weekday = "Monday";
        return this.nextWeekdayBrazil(options);

    }

    static nextTuesdayBrazil = (options = {}) =>{

        options.weekday = "Tuesday";
        return this.nextWeekdayBrazil(options);

    }

    static nextWednesdayBrazil = (options = {}) =>{

        options.weekday = "Wednesday";
        return this.nextWeekdayBrazil(options);

    }

    static nextThursdayBrazil = (options = {}) =>{

        options.weekday = "Thursday";
        return this.nextWeekdayBrazil(options);

    }

    static nextFridayBrazil = (options = {}) =>{

        options.weekday = "Friday";
        return this.nextWeekdayBrazil(options);

    }

    static nextSaturdayBrazil = (options = {}) =>{

        options.weekday = "Saturday";
        return this.nextWeekdayBrazil(options);

    }

    static nextSundayBrazil = (options = {}) =>{

        options.weekday = "Sunday";
        return this.nextWeekdayBrazil(options);

    }

    //==================================================================//
    //====================== Next [Day] UTC ============================//
    //==================================================================//

    static nextMondayUTC = (options = {}) =>{

        options.weekday = "Monday";
        return this.nextWeekdayUTC(options);

    }

    static nextTuesdayUTC = (options = {}) =>{

        options.weekday = "Tuesday";
        return this.nextWeekdayUTC(options);

    }

    static nextWednesdayUTC = (options = {}) =>{

        options.weekday = "Wednesday";
        return this.nextWeekdayUTC(options);

    }

    static nextThursdayUTC = (options = {}) =>{

        options.weekday = "Thursday";
        return this.nextWeekdayUTC(options);

    }

    static nextFridayUTC = (options = {}) =>{

        options.weekday = "Friday";
        return this.nextWeekdayUTC(options);

    }

    static nextSaturdayUTC = (options = {}) =>{

        options.weekday = "Saturday";
        return this.nextWeekdayUTC(options);

    }

    static nextSundayUTC = (options = {}) =>{

        options.weekday = "Sunday";
        return this.nextWeekdayUTC(options);

    }

    //==================================================================//
    //============== Next Wednesday Payment Day UTC ====================//
    //==================================================================//

    static nextPaymentWednesdayUTC = (options = {}) =>{

        //========================================================//
        // OPTIONS
        //========================================================//
        let {
            dateTime = this.dateTimeToday(),
        } = options;

        //========================================================//
        // GET WEEKDAY NAME
        //========================================================//
        let weekdayName = this.getWeekdayName({
            dateTime: dateTime,
        });

        //========================================================//
        // IF WEEKDAY IS INVALID
        //========================================================//
        if(!weekdayName){
            return "";
        }

        //========================================================//
        // DATE TIME UTC
        //========================================================//
        let dateTimeUTC = "";

        //========================================================//
        // IF WEEKDAY IS MONDAY / TUESDAY / WEDNESDAY
        //========================================================//
        if(
            weekdayName == "Monday"    ||
            weekdayName == "Tuesday"   ||
            weekdayName == "Wednesday"
        ){

            // GET NEXT WEDNESDAY (FROM NEXT WEEK)
            dateTimeUTC = this.nextWednesdayUTC({
                dateTime: dateTime,
                weeks: 1
            });

        }

        //========================================================//
        // IF WEEKDAY IS THURSDAY / FRIDAY
        //========================================================//
        if(
            weekdayName == "Thursday"  ||
            weekdayName == "Friday"
        ){

            // GET NEXT WEDNESDAY (FROM NEXT WEEK)
            dateTimeUTC = this.nextWednesdayUTC({
                dateTime:dateTime,
                weeks: 0
            });

        }

        //========================================================//
        // IF WEEKDAY IS SATURDAY / SUNDAY
        //========================================================//
        if(
            weekdayName == "Saturday" ||
            weekdayName == "Sunday"
        ){

            // GET NEXT WEDNESDAY (ONE WEEK AFTER NEXT WEEK)
            dateTimeUTC = this.nextWednesdayUTC({
                dateTime:dateTime,
                weeks: 1
            });

        }

        //========================================================//
        // IF DATE TIME UTC IS INVALID
        //========================================================//
        if(!dateTimeUTC){
            return  "";
        }

        return dateTimeUTC;

    };

    //==================================================================//
    //===================== Get Weekday Name ===========================//
    //==================================================================//

    static getWeekdayName = (options = {}) => {

        let {
            dateTime = this.dateTimeToday(),
            locale = 'en',
        } = options;

        dateTime = this.toDateTime({ dateTime });

        // Se for undefined.
        if(!dateTime){
            return "";
        }

        // Se for invalido.
        if(!dateTime?.isValid){
            return "";
        }

        return dateTime.setLocale(locale).toFormat('cccc');
        
    }

    //==================================================================//
    //==================== Operators [Simple] ==========================//
    //==================================================================//

    static greatherThan = (options = {}) =>{

        let {
            firstDateTime = "",
            secondDateTime = "",
        } = options;

        firstDateTime = this.toDateTime({dateTime:firstDateTime});
        secondDateTime = this.toDateTime({dateTime:secondDateTime});

        if(firstDateTime?.toMillis() > secondDateTime?.toMillis()){
            return true;
        }

        return false;

    }

    static greatherThanOrEqual = (options = {}) =>{

        let {
            firstDateTime = "",
            secondDateTime = "",
        } = options;

        firstDateTime = this.toDateTime({dateTime:firstDateTime});
        secondDateTime = this.toDateTime({dateTime:secondDateTime});

        if(firstDateTime?.toMillis() >= secondDateTime?.toMillis()){
            return true;
        }

        return false;

    }

    static lessThan = (options = {}) =>{

        let {
            firstDateTime = "",
            secondDateTime = "",
        } = options;

        firstDateTime = this.toDateTime({dateTime:firstDateTime});
        secondDateTime = this.toDateTime({dateTime:secondDateTime});

        if(firstDateTime?.toMillis() < secondDateTime?.toMillis()){
            return true;
        }

        return false;

    }

    static lessThanOrEqual = (options = {}) =>{

        let {
            firstDateTime = "",
            secondDateTime = "",
        } = options;

        firstDateTime = this.toDateTime({dateTime:firstDateTime});
        secondDateTime = this.toDateTime({dateTime:secondDateTime});

        if(firstDateTime?.toMillis() <= secondDateTime?.toMillis()){
            return true;
        }

        return false;

    }

    static equals = (options = {}) =>{

        let {
            firstDateTime = "",
            secondDateTime = "",
        } = options;

        firstDateTime = this.toDateTime({dateTime:firstDateTime});
        secondDateTime = this.toDateTime({dateTime:secondDateTime});

        if(firstDateTime?.toMillis() == secondDateTime?.toMillis()){
            return true;
        }

        return false;

    }

    //==================================================================//
    //=================== Operators [Advanced] =========================//
    //==================================================================//

    static greatherThanToday = (options = {}) =>{

        let {
            dateTime = "",
        } = options;

        let firstDateTime = this.toDateTime({dateTime});
        let secondDateTime = this.dateTimeToday();

        if(firstDateTime?.toMillis() > secondDateTime?.toMillis()){
            return true;
        }

        return false;

    }

    static lessThanOrEqualToToday = (options = {}) =>{

        let {
            dateTime = "",
        } = options;

        let firstDateTime = this.toDateTime({dateTime});
        let secondDateTime = this.dateTimeToday();

        if(firstDateTime?.toMillis() <= secondDateTime?.toMillis()){
            return true;
        }

        return false;

    }

}